import React, { useState } from 'react'
import { Modal, Button, Input, PasswordInput } from '@clickcease/cc-client-components-lib'
import { useTranslation } from 'react-i18next'
import { ReAuthModalBodyWrapper, ButtonWrapper, ErrorWrapper } from '../PendingInvitations.styled'
import { useLogin } from 'Features/auth/api/login'
import { useAppStore } from 'Stores/appStoreModuleFed'
import { useUserStore } from 'Stores/userStoreModuleFed'
import { useLoginUser } from 'Features/auth/hooks/login'
import { useNavigate } from 'react-router-dom'
import { GoogleSsoLogin } from 'Features/auth/components/GoogleSso/GoogleSsoLogin'
import { AxiosError } from 'axios'
import { ServerErrorType } from 'Types/ServerError'
import { useGetErrorText } from 'Utils/errorText'

interface ReauthModalProps {
  isOpen: boolean
}

export const ReAuthModal: React.FC<ReauthModalProps> = ({ isOpen }) => {
  const { t } = useTranslation('auth')
  const { setIsAuthenticating } = useAppStore()
  const { email } = useUserStore()
  const [password, setPassword] = useState<string>('')
  const [error, setError] = useState<string>('')
  const { mutate } = useLogin()
  const { login } = useLoginUser()
  const navigate = useNavigate()
  const { getErrorText } = useGetErrorText()

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPassword(event.target.value.trim())
  }

  const handleReauthenticate = () => {
    setIsAuthenticating(true)
    mutate(
      { email, password },
      {
        onSuccess: (res) => {
          setIsAuthenticating(false)
          setError('')
          const {
            token,
            userId,
            defaultAccount,
            userVerificationCodeRequired,
            subscriptionId,
            multiAccount,
            language,
          } = res.data
          login({
            token,
            userId,
            defaultAccount,
            userVerificationCodeRequired,
            subscriptionId,
            multiAccount,
            language,
          })
          navigate('/account-selector', { replace: true })
        },
        onError: () => {
          setError(t('login.auth_failed'))
        },
      }
    )
  }

  const onGoogleLoginError = (error: AxiosError<ServerErrorType>) => {
    let GoogleSSOErrorText = ''
    if (error.response?.data?.message === 'ACCESS_DENIED_INVALID_AUTH_METHOD') {
      GoogleSSOErrorText = t('errors.ACCESS_DENIED_INVALID_AUTH_METHOD_SSO', { ns: 'general' })
    } else {
      GoogleSSOErrorText = getErrorText(error) || ''
    }
    setError(GoogleSSOErrorText)
  }

  return (
    <Modal
      title={t('login.auth_required')}
      isOpen={isOpen}
      id="reauthentication-modal"
      style={{ zIndex: 50, width: 450 }}
    >
      <ReAuthModalBodyWrapper>
        <GoogleSsoLogin onError={onGoogleLoginError} source={'reAuthModal'} />
        <Input value={email} name="email" variant={'large'} disabled={true} />
        <PasswordInput
          value={password}
          name="password"
          variant={'large'}
          placeholder={t('login.password')}
          onChange={handleInputChange}
          showCheckSign={false}
        />
        {error && <ErrorWrapper>{error}</ErrorWrapper>}
        <ButtonWrapper>
          <Button text={t('login.confirm')} onClick={handleReauthenticate} />
        </ButtonWrapper>
      </ReAuthModalBodyWrapper>
    </Modal>
  )
}
